import { api } from './_client';
import type {
  OrganizationUsersResponse,
  PersonnelResponse,
  UpdateUserRolesRequest,
  UpdateUserRolesResponse,
  UserRole,
  OrganizationsResponse,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  UnassignedUsersResponse,
  AssignUserToOrganizationRequest,
  AssignUserToOrganizationResponse,
  CreateInvitationRequest,
  CreateInvitationResponse,
  PendingInvitation,
  PendingInvitationsResponse,
  InvitationValidation,
  AcceptAndCreateUserRequest,
  AcceptAndCreateUserResponse,
} from '../../types/api';

export const authApi = {
  // Get user info
  async getUserInfo() {
    const response = await api.get('/auth/user-info');
    return response.data;
  },

  // Get organization users (XER admin only)
  async getOrganizationUsers(): Promise<OrganizationUsersResponse> {
    const response = await api.get<OrganizationUsersResponse>('/auth/organization-users');
    return response.data;
  },

  // Get organization personnel for public overview (all org members can access)
  async getOrganizationPersonnel(roles?: string[]): Promise<PersonnelResponse> {
    const params = roles?.length ? `?roles=${roles.join('&roles=')}` : '';
    const response = await api.get<PersonnelResponse>(`/overview/personnel${params}`);
    return response.data;
  },

  // Get all organizations (XER admin only)
  async getAllOrganizations(): Promise<OrganizationsResponse> {
    const response = await api.get<OrganizationsResponse>('/auth/organizations');
    return response.data;
  },

  // Get users for a specific organization (XER admin only)
  async getOrganizationUsersById(organizationId: string): Promise<OrganizationUsersResponse> {
    const response = await api.get<OrganizationUsersResponse>(
      `/auth/organizations/${organizationId}/users`
    );
    return response.data;
  },

  // Create a new organization (XER admin only)
  async createOrganization(name: string): Promise<CreateOrganizationResponse> {
    const response = await api.post<CreateOrganizationResponse>('/auth/organizations', {
      name,
    } as CreateOrganizationRequest);
    return response.data;
  },

  // Get unassigned users (XER admin only)
  async getUnassignedUsers(): Promise<UnassignedUsersResponse> {
    const response = await api.get<UnassignedUsersResponse>('/auth/unassigned-users');
    return response.data;
  },

  // Assign a user to an organization (XER admin only)
  async assignUserToOrganization(
    userId: string,
    organizationId: string,
    roles: UserRole[]
  ): Promise<AssignUserToOrganizationResponse> {
    const response = await api.put<AssignUserToOrganizationResponse>(
      `/auth/user/${userId}/organization`,
      { organization_id: organizationId, roles } as AssignUserToOrganizationRequest
    );
    return response.data;
  },

  // Update user roles (XER admin only)
  async updateUserRoles(userId: string, roles: UserRole[]): Promise<UpdateUserRolesResponse> {
    const response = await api.put<UpdateUserRolesResponse>(`/auth/user/${userId}/roles`, {
      roles,
    } as UpdateUserRolesRequest);
    return response.data;
  },

  // === User Invitation Methods ===

  // Create a new user invitation (admin only)
  async createInvitation(email: string, roles: UserRole[]): Promise<CreateInvitationResponse> {
    const response = await api.post<CreateInvitationResponse>('/auth/invitations', {
      email,
      roles,
    } as CreateInvitationRequest);
    return response.data;
  },

  // Get pending invitations for the current organization (admin only)
  async getPendingInvitations(): Promise<PendingInvitation[]> {
    const response = await api.get<PendingInvitationsResponse>('/auth/invitations');
    return response.data.invitations;
  },

  // Validate an invitation token (public endpoint)
  async validateInvitation(token: string): Promise<InvitationValidation> {
    const response = await api.get<InvitationValidation>(`/auth/invitations/validate/${token}`);
    return response.data;
  },

  // Accept invitation and create user in one step (public endpoint)
  async acceptInvitationAndCreateUser(
    token: string,
    password: string,
    fullName: string
  ): Promise<AcceptAndCreateUserResponse> {
    const response = await api.post<AcceptAndCreateUserResponse>(
      '/auth/invitations/accept-and-create-user',
      {
        token,
        password,
        full_name: fullName,
      } as AcceptAndCreateUserRequest
    );
    return response.data;
  },

  // Revoke a pending invitation (admin only)
  async revokeInvitation(invitationId: string): Promise<void> {
    await api.delete(`/auth/invitations/${invitationId}`);
  },
};
